import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Form, Input, Modal, Select, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import DataTable from '../../components/DataTable';
import { orgApi, usersApi } from '../../api/admin.api';
import { toApiError } from '../../api/client';
import type { OperatorDto, TeamDto, UnitDto } from '../../types/admin';

export default function Organization() {
  const { message } = App.useApp();
  const qc = useQueryClient();

  const units = useQuery({ queryKey: ['units'], queryFn: orgApi.listUnits });
  const teams = useQuery({ queryKey: ['teams'], queryFn: orgApi.listTeams });
  const operators = useQuery({ queryKey: ['operators'], queryFn: orgApi.listOperators });
  const users = useQuery({ queryKey: ['users'], queryFn: usersApi.list });

  const [unitModal, setUnitModal] = useState(false);
  const [teamModal, setTeamModal] = useState(false);
  const [operatorModal, setOperatorModal] = useState(false);
  const [editingOperator, setEditingOperator] = useState<OperatorDto | null>(null);
  const [unitForm] = Form.useForm<{ name: string }>();
  const [teamForm] = Form.useForm<{ name: string; unitId: number }>();
  const [operatorForm] = Form.useForm<{
    name: string;
    code: string;
    teamId: number;
    userId: number;
  }>();

  const closeOperatorModal = () => {
    setOperatorModal(false);
    setEditingOperator(null);
    operatorForm.resetFields();
  };

  const openCreateOperatorModal = () => {
    setEditingOperator(null);
    operatorForm.resetFields();
    setOperatorModal(true);
  };

  const openEditOperatorModal = (operator: OperatorDto) => {
    setEditingOperator(operator);
    operatorForm.setFieldsValue({
      name: operator.name,
      code: operator.code,
      teamId: operator.teamId,
    });
    setOperatorModal(true);
  };

  const unitName = useMemo(() => {
    const map = new Map((units.data ?? []).map((u) => [u.id, u.name]));
    return (id: number) => map.get(id) ?? `#${id}`;
  }, [units.data]);

  const eligibleOperatorUsers = useMemo(() => {
    const linkedUserIds = new Set(
      (operators.data ?? [])
        .map((operator) => operator.userId)
        .filter((userId): userId is number => userId !== null),
    );
    return (users.data ?? []).filter((user) => user.isActive && !linkedUserIds.has(user.id));
  }, [operators.data, users.data]);

  const onError = (err: unknown) => message.error(toApiError(err).message);

  const createUnit = useMutation({
    mutationFn: orgApi.createUnit,
    onSuccess: () => {
      message.success('واحد ایجاد شد');
      setUnitModal(false);
      unitForm.resetFields();
      void qc.invalidateQueries({ queryKey: ['units'] });
    },
    onError,
  });

  const createTeam = useMutation({
    mutationFn: orgApi.createTeam,
    onSuccess: () => {
      message.success('تیم ایجاد شد');
      setTeamModal(false);
      teamForm.resetFields();
      void qc.invalidateQueries({ queryKey: ['teams'] });
    },
    onError,
  });

  const createOperator = useMutation({
    mutationFn: orgApi.createOperator,
    onSuccess: () => {
      message.success('اپراتور ایجاد شد');
      closeOperatorModal();
      void qc.invalidateQueries({ queryKey: ['operators'] });
    },
    onError,
  });

  const updateOperator = useMutation({
    mutationFn: ({ id, values }: {
      id: number;
      values: { name: string };
    }) => orgApi.updateOperator(id, values),
    onSuccess: () => {
      message.success('اپراتور ویرایش شد');
      closeOperatorModal();
      void qc.invalidateQueries({ queryKey: ['operators'] });
    },
    onError,
  });

  const assignTeam = useMutation({
    mutationFn: ({ id, teamId }: { id: number; teamId: number }) =>
      orgApi.assignOperatorTeam(id, { teamId }),
    onSuccess: () => {
      message.success('اپراتور به تیم تخصیص یافت');
      void qc.invalidateQueries({ queryKey: ['operators'] });
    },
    onError,
  });

  const unitCols: ColumnsType<UnitDto> = [
    { title: 'شناسه', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'نام واحد', dataIndex: 'name', key: 'name' },
  ];

  const teamCols: ColumnsType<TeamDto> = [
    { title: 'شناسه', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'نام تیم', dataIndex: 'name', key: 'name' },
    { title: 'واحد', key: 'unit', render: (_, t) => unitName(t.unitId) },
  ];

  const operatorCols: ColumnsType<OperatorDto> = [
    { title: 'نام اپراتور', dataIndex: 'name', key: 'name' },
    { title: 'کد', dataIndex: 'code', key: 'code' },
    {
      title: 'کاربر سامانه',
      key: 'user',
      render: (_, operator) => operator.userId === null
        ? 'بدون کاربر (قدیمی)'
        : (
          <Space direction="vertical" size={0}>
            <span>{operator.userDisplayName}</span>
            <span>{operator.userEmail}</span>
          </Space>
        ),
    },
    { title: 'واحد', key: 'unit', render: (_, o) => unitName(o.unitId) },
    {
      title: 'تیم',
      key: 'team',
      render: (_, o) => (
        <Select
          style={{ minWidth: 180 }}
          value={o.teamId}
          loading={assignTeam.isPending}
          options={(teams.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
          onChange={(teamId) => assignTeam.mutate({ id: o.id, teamId })}
        />
      ),
    },
    {
      title: 'عملیات',
      key: 'actions',
      render: (_, operator) => (
        <Button type="link" onClick={() => openEditOperatorModal(operator)}>
          ویرایش
        </Button>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ display: 'flex' }}>
      <Card
        title="واحدها"
        extra={<Button type="primary" onClick={() => setUnitModal(true)}>واحد جدید</Button>}
      >
        <DataTable<UnitDto> columns={unitCols} dataSource={units.data ?? []} loading={units.isLoading} />
      </Card>

      <Card
        title="تیم‌ها"
        extra={<Button type="primary" onClick={() => setTeamModal(true)}>تیم جدید</Button>}
      >
        <DataTable<TeamDto> columns={teamCols} dataSource={teams.data ?? []} loading={teams.isLoading} />
      </Card>

      <Card
        title="اپراتورها"
        extra={<Button type="primary" onClick={openCreateOperatorModal}>اپراتور جدید</Button>}
      >
        <DataTable<OperatorDto>
          columns={operatorCols}
          dataSource={operators.data ?? []}
          loading={operators.isLoading}
        />
      </Card>

      <Modal
        title="واحد جدید"
        open={unitModal}
        onOk={() => unitForm.submit()}
        onCancel={() => setUnitModal(false)}
        confirmLoading={createUnit.isPending}
        okText="ذخیره"
        cancelText="انصراف"
        destroyOnHidden
      >
        <Form form={unitForm} layout="vertical" onFinish={(v) => createUnit.mutate(v)}>
          <Form.Item name="name" label="نام واحد" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="تیم جدید"
        open={teamModal}
        onOk={() => teamForm.submit()}
        onCancel={() => setTeamModal(false)}
        confirmLoading={createTeam.isPending}
        okText="ذخیره"
        cancelText="انصراف"
        destroyOnHidden
      >
        <Form form={teamForm} layout="vertical" onFinish={(v) => createTeam.mutate(v)}>
          <Form.Item name="name" label="نام تیم" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="unitId" label="واحد" rules={[{ required: true }]}>
            <Select
              style={{ width: '100%' }}
              loading={units.isLoading}
              options={(units.data ?? []).map((u) => ({ value: u.id, label: u.name }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingOperator ? 'ویرایش اپراتور' : 'اپراتور جدید'}
        open={operatorModal}
        onOk={() => operatorForm.submit()}
        onCancel={closeOperatorModal}
        confirmLoading={createOperator.isPending || updateOperator.isPending}
        okText="ذخیره"
        cancelText="انصراف"
        destroyOnHidden
      >
        <Form
          form={operatorForm}
          layout="vertical"
          onFinish={(values) => {
            if (editingOperator) {
              updateOperator.mutate({ id: editingOperator.id, values: { name: values.name } });
            } else {
              createOperator.mutate(values);
            }
          }}
        >
          <Form.Item
            name="name"
            label="نام اپراتور"
            rules={[{ required: true, message: 'نام اپراتور اجباری است' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="code"
            label="کد اپراتور"
            rules={[{ required: true, message: 'کد اپراتور اجباری است' }]}
          >
            <Input maxLength={450} showCount disabled={editingOperator !== null} />
          </Form.Item>
          <Form.Item
            name="teamId"
            label="تیم"
            rules={[{ required: true, message: 'تیم اجباری است' }]}
          >
            <Select
              style={{ width: '100%' }}
              disabled={editingOperator !== null}
              loading={teams.isLoading}
              options={(teams.data ?? []).map((team) => ({
                value: team.id,
                label: `${team.name} — ${unitName(team.unitId)}`,
              }))}
            />
          </Form.Item>
          {!editingOperator && (
            <Form.Item
              name="userId"
              label="کاربر سامانه"
              rules={[{ required: true, message: 'کاربر سامانه اجباری است' }]}
            >
              <Select
                style={{ width: '100%' }}
                loading={users.isLoading || operators.isLoading}
                options={eligibleOperatorUsers.map((user) => ({
                  value: user.id,
                  label: `${user.name} ${user.familyName} — ${user.email}`,
                }))}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Space>
  );
}
